import { useState, useEffect } from "react";

/**
 * Хук useKanbanVisibleStatuses
 *
 * Назначение:
 * Управляет видимостью статусов в Kanban-доске. Позволяет пользователю выбирать, какие колонки статусов
 * отображать на доске, и сохраняет эти настройки в localStorage для каждой комбинации пользователь/РГ/режим.
 *
 * Что делает:
 * 1. Загружает сохранённые настройки видимости статусов из localStorage
 * 2. Инициализирует статусы по умолчанию (все, кроме черновиков и удалённых)
 * 3. Сохраняет изменения видимости статусов в localStorage
 * 4. Предоставляет обработчик для управления чекбоксами видимости статусов
 *
 * Логика работы:
 * - При первом запуске (нет данных в localStorage) показываются все статусы,
 *   кроме помеченных как is_for_draft и is_for_deletion
 * - При последующих запусках восстанавливается сохранённое состояние
 * - Каждая комбинация (рабочая группа + пользователь + режим админа) имеет своё хранилище
 * - При изменении списка statuses происходит повторная инициализация
 *
 * Формат ключа localStorage:
 * `kanban_visible_statuses_{unit_uuid}_{user_uuid}_{isAdmins}`
 *
 * @param {Array} statuses - список всех доступных статусов
 * @param {string} unit_uuid - UUID текущей рабочей группы
 * @param {string} user_uuid - UUID текущего пользователя
 * @param {boolean} isAdmins - флаг админского режима
 *
 * @returns {Object} Объект с состоянием и функциями управления видимостью:
 *   - visibleStatuses: Array<string> - массив UUID видимых статусов
 *   - saveVisibleStatuses: Function - функция сохранения списка видимых статусов
 *   - handleStatusCheckboxChange: Function - обработчик изменения чекбокса статуса
 */
export const useKanbanVisibleStatuses = (
  statuses,
  unit_uuid,
  user_uuid,
  isAdmins
) => {
  const [visibleStatuses, setVisibleStatuses] = useState([]);

  // Функция генерации ключа localStorage
  // Создаёт уникальный ключ для каждой комбинации РГ/пользователь/режим
  const getStorageKey = () => {
    return `kanban_visible_statuses_${unit_uuid}_${user_uuid}_${isAdmins}`;
  };

  // Инициализация статусов по умолчанию
  // Включает все статусы, кроме черновиков и удалённых
  const initializeDefaultStatuses = () => {
    const defaultStatuses = statuses
      .filter(
        (status) =>
          !status.is_for_draft &&
          !status.is_for_deletion &&
          !status.is_for_cancellation,
      )
      .map((status) => status.uuid);
    setVisibleStatuses(defaultStatuses);
  };

  // Загрузка статусов из localStorage или инициализация по умолчанию
  // Запускается при изменении списка статусов или параметров контекста
  useEffect(() => {
    if (statuses.length === 0) return;

    const storageKey = getStorageKey();
    const saved = localStorage.getItem(storageKey);

    if (saved) {
      try {
        setVisibleStatuses(JSON.parse(saved));
      } catch {
        // При ошибке парсинга используем значения по умолчанию
        initializeDefaultStatuses();
      }
    } else {
      initializeDefaultStatuses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statuses, unit_uuid, user_uuid, isAdmins]);

  // Сохранение видимых статусов в localStorage и обновление состояния
  const saveVisibleStatuses = (uuids) => {
    const storageKey = getStorageKey();
    localStorage.setItem(storageKey, JSON.stringify(uuids));
    setVisibleStatuses(uuids);
  };

  // Обработчик изменения чекбокса статуса
  // Добавляет или удаляет статус из списка видимых
  const handleStatusCheckboxChange = (statusUuid, checked) => {
    if (checked) {
      // Добавляем статус в список видимых
      saveVisibleStatuses([...visibleStatuses, statusUuid]);
    } else {
      // Удаляем статус из списка видимых
      saveVisibleStatuses(
        visibleStatuses.filter((uuid) => uuid !== statusUuid)
      );
    }
  };

  return {
    visibleStatuses,
    saveVisibleStatuses,
    handleStatusCheckboxChange,
  };
};
